


import type { UnitStatReportViewModel } from 'src/api/types/UnitStatReportViewModel';
import type { ClassCodeLookupOption, CodeDescriptionLookupOption } from 'src/api/services/policy.service';

import { useMemo, Fragment } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Box, Table, Button, TableRow, TableHead, TableCell, TableBody, Typography, TableContainer } from '@mui/material';

import { NCRBHorizontalDivider } from 'src/components/ncrb-custom/ncrb-horizontal-divider';

import UnitStatExposuresTable from './components/unit-stat-exposures-table';
import { buildTopLevelTableRow, groupExposuresBySplitPeriods } from './utils';
import { UnitStatExposuresTotals } from './components/unit-stat-exposures-totals';
import UnitStatExposuresEditSplitRow from './components/unit-stat-exposures-edit-split-row';

import type { USRExposuresTotals } from './types';
import type { UnitStatExposureDraftDelta } from './unit-stat-exposures-edit.helpers';


type UnitStatExposuresProps = {
  data?: UnitStatReportViewModel;
  isEditMode?: boolean;
  draft?: UnitStatExposureDraftDelta;
  setDraft?: Dispatch<SetStateAction<UnitStatExposureDraftDelta>>;
  errors?: Record<string, string>;
  updateTypes?: CodeDescriptionLookupOption[];
  classCodes?: ClassCodeLookupOption[];
  splitPeriodTypes?: CodeDescriptionLookupOption[];
};

export default function UnitStatExposures({
  data: unitStatReport,
  isEditMode = false,
  draft,
  setDraft,
  errors = {},
  updateTypes = [],
  classCodes = [],
  splitPeriodTypes = [],
}: UnitStatExposuresProps) {

  const { rows: rowsData, totals: totalsData } = useMemo(() => {

    if (!unitStatReport) {
      return { rows: [], totals: {} };
    }

    const rows = groupExposuresBySplitPeriods(unitStatReport?.unitStatReportExposures ?? []);
    let modifiedPremiumTotal = 0;
    for (const row of rows) {
      modifiedPremiumTotal += row.modifiedPremium ?? 0;
    }

    const totals = {
      subjectPremiumTotal: 0,
      standardExposureTotal: 0,
      standardPremiumTotal: 0,
      modifiedPremiumTotal,
    } satisfies USRExposuresTotals;

    for (const usrUnitTotals of unitStatReport.unitStatReportUnitTotals ?? []) {
      totals.subjectPremiumTotal += usrUnitTotals.subjectPremiumTotal ?? 0;
      totals.standardExposureTotal += usrUnitTotals.exposureTotal ?? 0;
      totals.standardPremiumTotal += usrUnitTotals.standardPremiumTotal ?? 0;
    }

    return {
      rows,
      totals,
    };
  }, [unitStatReport]);

  if (!unitStatReport) {
    return null;
  }

  if (isEditMode && draft && setDraft) {
    const splitCodes = Array.from(new Set(draft.rows.map((row) => row.splitPeriodCode))).sort();
    const latestSplit = splitCodes[splitCodes.length - 1];
    const maxSplitCount = splitPeriodTypes.length > 0 ? splitPeriodTypes.length : 10;
    // The reference table (UnitStatReportExposureSplitPeriodType) is already ordered by SplitPeriodCode,
    // so the next split is whichever entry immediately follows the highest split code already in use.
    const splitPeriodDescriptionByCode = new Map(splitPeriodTypes.map((option) => [option.code, option.description]));
    const getNextSplitPeriod = (usedSplitCodes: string[]) => {
      if (splitPeriodTypes.length === 0) {
        const nextCode = String(Math.max(0, ...usedSplitCodes.map((code) => Number(code) || 0)) + 1);
        return { code: nextCode, description: undefined as string | undefined };
      }
      const usedCodes = new Set(usedSplitCodes);
      const lastUsedIndex = splitPeriodTypes.reduce(
        (lastIndex, option, index) => (usedCodes.has(option.code) ? index : lastIndex),
        -1
      );
      const nextOption = splitPeriodTypes[lastUsedIndex + 1];
      return nextOption ? { code: nextOption.code, description: nextOption.description } : null;
    };
    const deletedExposureIdSet = new Set(draft.deletedExposureIds);
    const splitSummaryByCode = new Map<string, ReturnType<typeof buildTopLevelTableRow>>();
    const exposuresBySplitCode = new Map<string, NonNullable<typeof unitStatReport.unitStatReportExposures>>();
    // Exclude exposures deleted in this draft session, so a split code reused by a brand-new split
    // doesn't inherit stale totals from the split that was just removed.
    for (const exposure of unitStatReport.unitStatReportExposures ?? []) {
      if (exposure.usrexposureId !== undefined && deletedExposureIdSet.has(exposure.usrexposureId)) {
        continue;
      }
      const code = exposure.splitPeriod?.splitPeriodCode ?? '';
      if (!exposuresBySplitCode.has(code)) exposuresBySplitCode.set(code, []);
      exposuresBySplitCode.get(code)!.push(exposure);
    }
    for (const [code, exposures] of exposuresBySplitCode) {
      splitSummaryByCode.set(code, buildTopLevelTableRow(exposures[0]?.splitPeriod, exposures));
    }
    const createNewRow = (splitPeriodCode: string) => ({
      clientId: `new-${Date.now()}-${Math.random()}`,
      classCode: '',
      expModFactor: '',
      expModFactorEffectiveDate: '',
      rateEffectiveDate: '',
      exposure: '',
      manualRate: '',
      premium: '',
      splitPeriodCode,
      updateTypeCode: '',
      isNew: true,
    });
    const addSplit = () => {
      setDraft((currentDraft) => {
        // Reusing a code freed up by a deletion is intentional here (it keeps split numbering
        // contiguous); the new rows are still distinct isNew entries and the original exposures
        // stay tracked in deletedExposureIds, so a save can't conflate the two splits.
        const activeSplitCodes = Array.from(new Set(currentDraft.rows.map((row) => row.splitPeriodCode))).sort();
        const nextSplitPeriod = getNextSplitPeriod(activeSplitCodes);
        if (!nextSplitPeriod || activeSplitCodes.length >= maxSplitCount) {
          return currentDraft;
        }
        return {
          ...currentDraft,
          rows: [createNewRow(nextSplitPeriod.code), ...currentDraft.rows],
        };
      });
      requestAnimationFrame(() => {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
      });
    };
    const addExposure = (splitPeriodCode: string) => {
      const newRow = createNewRow(splitPeriodCode);
      setDraft((currentDraft) => ({
        ...currentDraft,
        rows: [...currentDraft.rows, newRow],
      }));
      requestAnimationFrame(() => {
        document.getElementById(`new-exposure-form-${newRow.clientId}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
    };
    const updateRow = (clientId: string, field: string, value: string) =>
      setDraft((currentDraft) => ({
        ...currentDraft,
        rows: currentDraft.rows.map((row) => (row.clientId === clientId ? { ...row, [field]: value } : row)),
      }));
    // Only Exposure is gated by the selected class code's subjectToExperienceMod flag;
    // Experience mod remains user-editable regardless of the class code.
    const isSubjectToExperienceMod = (classCode: string) =>
      classCodes.find((option) => option.classCode === classCode)?.subjectToExperienceMod ?? false;
    const updateClassCode = (clientId: string, classCode: string) => {
      const subjectToExperienceMod = isSubjectToExperienceMod(classCode);
      setDraft((currentDraft) => ({
        ...currentDraft,
        rows: currentDraft.rows.map((row) =>
          row.clientId === clientId
            ? {
                ...row,
                classCode,
                exposure: subjectToExperienceMod ? row.exposure : '',
              }
            : row
        ),
      }));
    };
    const removeRow = (clientId: string) => {
      setDraft((currentDraft) => {
        const row = currentDraft.rows.find((item) => item.clientId === clientId);
        return {
          ...currentDraft,
          rows: currentDraft.rows.filter((item) => item.clientId !== clientId),
          deletedExposureIds:
            row?.usrexposureId
              ? [...currentDraft.deletedExposureIds, row.usrexposureId]
              : currentDraft.deletedExposureIds,
        };
      });
    };
    const removeSplit = (splitPeriodCode: string) => {
      setDraft((currentDraft) => {
        const rows = currentDraft.rows.filter((row) => row.splitPeriodCode !== splitPeriodCode);
        const deletedExposureIds = currentDraft.rows
          .filter((row) => row.splitPeriodCode === splitPeriodCode && row.usrexposureId)
          .map((row) => row.usrexposureId as number);
        return { ...currentDraft, rows, deletedExposureIds: [...currentDraft.deletedExposureIds, ...deletedExposureIds] };
      });
    };

    return (
      <Box sx={{ paddingTop: '1.5rem' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" color="text.primary" data-testid="unit-stat-exposures-title">
            Splits
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={addSplit}
            disabled={splitCodes.length >= maxSplitCount}
            data-testid="add-split"
            startIcon={<AddIcon sx={{ color: '#2092BA' }} />}
            sx={(theme) => ({
              color: '#1C252E',
              fontFamily: theme.typography.button.fontFamily,
              fontWeight: theme.typography.fontWeightMedium,
              fontStyle: 'normal',
              fontSize: theme.typography.button.fontSize,
              lineHeight: theme.typography.button.lineHeight,
              letterSpacing: 0,
              textAlign: 'center',
              verticalAlign: 'middle',
              textTransform: 'none',
            })}
          >
            Add split
          </Button>
        </Box>
        <TableContainer>
          <Table data-testid="unit-stat-exposures-edit-table" size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Split indicator</TableCell>
                <TableCell>Subject premium</TableCell>
                <TableCell>Modified premium</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {splitCodes.map((splitCode, index) => {
                const splitRows = draft.rows.filter((row) => row.splitPeriodCode === splitCode);
                const newRows = splitRows.filter((row) => row.isNew);
                const existingRows = splitRows.filter((row) => !row.isNew);
                const isNewSplit = index > 0 && newRows.length > 0 && existingRows.length === 0;
                const committedSummary = splitSummaryByCode.get(splitCode);
                const summary = {
                  splitIndicator: committedSummary?.splitIndicator ?? splitPeriodDescriptionByCode.get(splitCode) ?? null,
                  subjectPremium: committedSummary?.subjectPremium ?? 0,
                  modifiedPremium: committedSummary?.modifiedPremium ?? 0,
                };
                const canDeleteSplit = splitCode === latestSplit && splitCodes.length > 1;
                return (
                  <Fragment key={splitCode}>
                    {isNewSplit && (
                      <TableRow data-testid={`new-split-divider-${splitCode}`}>
                        <TableCell
                          colSpan={5}
                          sx={{ height: 8, p: 0, borderBottom: 0, backgroundColor: '#F4F6F8' }}
                        />
                      </TableRow>
                    )}
                    <UnitStatExposuresEditSplitRow
                      splitCode={splitCode}
                      summary={summary}
                      existingRows={existingRows}
                      newRows={newRows}
                      canDeleteSplit={canDeleteSplit}
                      errors={errors}
                      updateTypes={updateTypes}
                      classCodes={classCodes}
                      onAddExposure={addExposure}
                      onRemoveSplit={removeSplit}
                      onRemoveRow={removeRow}
                      onUpdateRow={updateRow}
                      onUpdateClassCode={updateClassCode}
                    />
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ paddingTop: '1.5rem' }}>
      <UnitStatExposuresTable
        dataTestId="unit-stat-exposures-table"
        title="Splits"
        columns={['Split indicator', 'Subject premium', 'Modified premium']}
        rows={rowsData}
        exposuresAreHidden={(unitStatReport?.reportNumber ?? 0) >= 2}
      />
      <Box pt={5} pb={5}>
        <NCRBHorizontalDivider />
      </Box>
      <UnitStatExposuresTotals data={totalsData} />
    </Box>
  );
}

